import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { usePurchaseConsigneeSellerAccountId } from 'src/contexts/PurchaseConsigneeContext';
import {
  PurchaseOrderSettings,
  SellerAccountClient,
} from 'src/WebApiController';

export const usePurchaseOrderSettings = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  // Inside a purchase wizard creating for a consignee, these are the consignee's settings
  const consigneeSellerAccountId = usePurchaseConsigneeSellerAccountId();

  const queryClient = useQueryClient();
  const queryKey = [
    'SellerAccountClient.getPurchaseOrderSettings',
    activeAccountWebClientConfig.activeAccountId,
    consigneeSellerAccountId,
  ];

  const shouldQuery = activeAccountWebClientConfig.activeAccountId != null;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).getPurchaseOrderSettings(consigneeSellerAccountId);
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async (settings: PurchaseOrderSettings) => {
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).mergePurchaseOrderSettings(settings);
    },
    onError: (err: ErrorTypes, settings) => {
      showErrorDialog('SellerAccountClient.mergePurchaseOrderSettings', err, {
        trackErrorData: { settings },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    mutation,
  };
};
